import { DragDropModule } from '@angular/cdk/drag-drop';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { CommonModule } from '@angular/common';
import { CUSTOM_ELEMENTS_SCHEMA, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { NotificationCardComponent } from '@app/energy-guard/shared/components/notification-card/notification-card.component';
import { ShieldGuardIconComponent } from '@app/energy-guard/shared/components/shield-guard-icon/shield-guard-icon.component';
import { MeterConsumptionDropdownFilterComponent } from '@app/meter-consumption/components/meter-consumption-dropdown-filter/meter-consumption-dropdown-filter.component';
import { MeterHeaderIconComponent } from '@app/meter-consumption/components/meter-header-icon/meter-header-icon.component';
import { MeterHeaderItemComponent } from '@app/meter-consumption/components/meter-header-item/meter-header-item.component';
import { MeterHeaderComponent } from '@app/meter-consumption/components/meter-header/meter-header.component';
import { ButtonSpinnerComponent } from '@app/shared/components/button-spinner/button-spinner.component';
import { ExistingWoWarningComponent } from '@app/socket-service-page/components/existing-wo-warning/existing-wo-warning.component';
import { TranslateModule } from '@ngx-translate/core';
import { FileUploadModule } from 'ng2-file-upload';
import { ColorPickerModule } from 'ngx-color-picker';
import { CalendarModule } from 'primeng/calendar';
import { EditorModule } from 'primeng/editor';
import { PaginatorModule } from 'primeng/paginator';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ContactInfoComponent } from '../contact-page/components/contact-info/contact-info.component';
import { AddressSearchComponent } from './components/address-search/address-search.component';
import { BackButtonComponent } from './components/back-button/back-button.component';
import { BannerAdvertisementComponent } from './components/banner-advertisement/banner-advertisement.component';
import { ClickToggleComponent } from './components/click-toggle/click-toggle.component';
import { CollapsibleRowComponent } from './components/collapsible-row/collapsible-row.component';
import { ConsumptionFooterComponent } from './components/consumption-footer/consumption-footer.component';
import { CustomTooltipComponent } from './components/custom-tooltip/custom-tooltip.component';
import { DashboardPanelComponent } from './components/dashboard-panel/dashboard-panel.component';
import { DescriptionSnackbarComponent } from './components/description-snackbar/description-snackbar.component';
import { DesignSettingsMenuComponent } from './components/design-settings-menu/design-settings-menu.component';
import { DropdownOptionComponent } from './components/dropdown-option/dropdown-option.component';
import { DropdownSelectionComponent } from './components/dropdown-selection/dropdown-selection.component';
import { DropdownComponent } from './components/dropdown/dropdown.component';
import { DsoDropdownComponent } from '../dso/components/dso-dropdown/dso-dropdown.component';
import { ErrorBannerComponent } from './components/error-banner/error-banner.component';
import { ExpansionPanelComponent } from './components/expansion-panel/expansion-panel.component';
import { FilterOptionsComponent } from './components/filter-options/filter-options.component';
import { FooterComponent } from './components/footer/footer.component';
import { HeaderDropdownComponent } from './components/header-dropdown/header-dropdown.component';
import { HeaderComponent } from './components/header/header.component';
import { InputGroupComponent } from './components/input-group/input-group.component';
import { InputLabelListComponent } from './components/input-label-list/input-label-list.component';
import { LineGaugeChartRainbowComponent } from './components/line-gauge-chart-rainbow/line-gauge-chart-rainbow.component';
import { LineGaugeChartTriColorComponent } from './components/line-gauge-chart-tri-color/line-gauge-chart-tri-color.component';
import { ListTreeComponent } from './components/list-tree/list-tree.component';
import { LongTextPopoverComponent } from './components/long-text-popover/long-text-popover.component';
import { DateSelectionComponent } from './components/meter-download-wizard/date-selection/date-selection.component';
import { FileExtensionSelectionComponent } from './components/meter-download-wizard/file-extension-selection/file-extension-selection.component';
import { GranularitySelectionComponent } from './components/meter-download-wizard/granularity-selection/granularity-selection.component';
import { MeterDownloadWizardComponent } from './components/meter-download-wizard/meter-download-wizard.component';
import { MeterListSelectionComponent } from './components/meter-download-wizard/meter-list-selection/meter-list-selection.component';
import { MeterSelectionComponent } from './components/meter-download-wizard/meter-selection/meter-selection.component';
import { MeterTableComponent } from './components/meter-table/meter-table.component';
import { MeterWizardCollapsibleRowComponent } from './components/meter-wizard-collapsible-row/meter-wizard-collapsible-row.component';
import { MultipurposeDropdownComponent } from './components/multipurpose-dropdown/multipurpose-dropdown.component';
import { MunicipalitySelectionComponent } from './components/municipality-selection/municipality-selection.component';
import { NotePopUpComponent } from './components/note-popup/note-pop-up.component';
import { OverlayLoaderComponent } from './components/overlay-loader/overlay-loader.component';
import { PaginatorComponent } from './components/paginator/paginator.component';
import { PasswordRequirementsComponent } from './components/password-requirements/password-requirements.component';
import { ProgressBarComponent } from './components/progress-bar/progress-bar.component';
import { SidebarComponent } from './components/sidebar/sidebar.component';
import { SkeletonLoaderComponent } from './components/skeleton-loader/skeleton-loader.component';
import { StatusSnackBarComponent } from './components/snack-bar/status-snack-bar.component';
import { SplitButtonComponent } from './components/split-button/split-button.component';
import { StatusChipComponent } from './components/status-chip/status-chip.component';
import { SvgIconComponent } from './components/svg-icon/svg-icon.component';
import { UploadBoxComponent } from './components/upload-box/upload-box.component';
import { UserProfileMenuComponent } from './components/user-profile-menu/user-profile-menu.component';
import { ConfirmationDialogV2Component } from './dialog/confirmation-dialog-v2/confirmation-dialog-v2.component';
import { ConfirmationDialogComponent } from './dialog/confirmation-dialog/confirmation-dialog.component';
import { DialogComponent } from './dialog/dialog.component';
import { EpGenericDialogComponent } from './dialog/ep-generic-dialog/ep-generic-dialog.component';
import { InformationDialogComponent } from './dialog/information-dialog/information-dialog.component';
import { ListTreeDialogComponent } from './dialog/list-tree-dialog/list-tree-dialog.component';
import { NotificationDialogComponent } from './dialog/notification-dialog/notification-dialog.component';
import { ProgressDialogComponent } from './dialog/progress-dialog/progress-dialog.component';
import { DigitOnlyDirective } from './directives/digit-only.directive';
import { DragndropDirective } from './directives/dragndrop.directive';
import { DropzoneDirective } from './directives/dropzone.directive';
import { MatTooltipOnEllipsisDirective } from './directives/mat-tooltip-on-ellipsis.directive';
import { StickyContainerDirective } from './directives/sticky-container.directive';
import { MaterialModule } from './material.module';
import { GetAddressPipe } from './pipes/get-address.pipe';
import { GetMeterAddressPipe } from './pipes/get-meter-address.pipe';
import { GetAddressGpsPipe } from './pipes/get-address-gps.pipe';
import { GetEnergyTypeImagePipe } from './pipes/get-energy-type-image.pipe';
import { GetLocationNamePipe } from './pipes/get-location-name-pipe';
import { GetMeterTypeImagePipe } from './pipes/get-meter-type-image.pipe';
import { LocalDatePipe } from './pipes/local-date.pipe';
import { LocalNumberPipe } from './pipes/local-number.pipe';
import { NoCommaPipe } from './pipes/no-comma.pipe';
import { NoLeadingZeroPipe } from './pipes/no-leading-zero.pipe';
import { ReplaceAllPipe } from './pipes/replace-all.pipe';
import { TermsOfUseDialogComponent } from './dialog/terms-of-use-dialog/terms-of-use-dialog.component';
import { UrlSanitizerPipe } from './pipes/url-sanitizer.pipe';
import { EpInputIndicatorsComponent } from './components/ep-input-indicators/ep-input-indicators.component';
import { IconDropdownComponent } from './components/icon-dropdown/icon-dropdown.component';
import { EpSearchComponent } from './components/ep-search/ep-search.component';
import { ErrorNoticeComponent } from './components/error-notice/error-notice.component';
import { SuccessNoticeComponent } from './components/success-notice/success-notice.component';
import { InformationNoticeComponent } from './components/information-notice/information-notice.component';
import { WarningNoticeComponent } from './components/warning-notice/warning-notice.component';
import { EpDropdownOptionComponent } from './components/ep-dropdown-option/ep-dropdown-option.component';
import { EpDropdownComponent } from './components/ep-dropdown/ep-dropdown.component';
import { TermsAndConditionsDialogComponent } from './dialog/terms-and-conditions-dialog/terms-and-conditions-dialog.component';
import { EpCheckboxComponent } from './components/ep-checkbox/ep-checkbox.component';
import { GeneralErrorDialogComponent } from './dialog/general-error-dialog/general-error-dialog.component';
import { ErrorDialogComponent } from './dialog/error-dialog/error-dialog.component';
import { ReusableTooltipComponent } from '../shared/tooltip/reusable-tooltip/reusable-tooltip.component';
import { ReusableTooltipDirective } from './directives/reusable-tooltip.directive';
import { EpInputFormFieldComponent } from './components/ep-input-form-field/ep-input-form-field.component';
import { EpDateFormFieldComponent } from './components/ep-date-form-field/ep-date-form-field.component';
import { GetObisPipe } from './pipes/get-obis.pipe';
import { GetTariffTypePipe } from './pipes/get-tariff-type.pipe';
import { InterpolatePipe } from './pipes/interpolate.pipe';
import { SafePipe } from './pipes/safe.pipe';
import { EpToggleSwitchComponent } from './components/ep-toggle-switch/ep-toggle-switch.component';
import { FeaturesIconAppComponent } from './components/features-icon-app/features-icon-app.component';
import { AutofocusDirective } from './directives/autofocus.directive';
import { LazyLoadImgDirective } from './directives/lazy-load-img.directive';
import { EpFormSelectDropdownComponent } from './components/ep-form-select-dropdown/ep-form-select-dropdown.component';
import { ContinueEditingDialogComponent } from './dialog/continue-editing-dialog/continue-editing-dialog.component';
import { MeterDataInfoDialogComponent } from '@app/shared/dialog/meter-data-info-dialog/meter-data-info-dialog.component';
import { EpCardComponent } from './components/ep-card/ep-card.component';
import { EpCardFooterComponent } from './components/ep-card-footer/ep-card-footer.component';

@NgModule({
  declarations: [
    //#region Components
    AddressSearchComponent,
    BackButtonComponent,
    BannerAdvertisementComponent,
    ButtonSpinnerComponent,
    ClickToggleComponent,
    CollapsibleRowComponent,
    ConsumptionFooterComponent,
    ConfirmationDialogComponent,
    ContactInfoComponent,
    CustomTooltipComponent,
    EpInputIndicatorsComponent,
    EpSearchComponent,
    DashboardPanelComponent,
    DateSelectionComponent,
    DesignSettingsMenuComponent,
    DescriptionSnackbarComponent,
    DialogComponent,
    EpGenericDialogComponent,
    ContinueEditingDialogComponent,
    DropdownComponent,
    DropdownOptionComponent,
    EpDropdownComponent,
    EpDropdownOptionComponent,
    DropdownSelectionComponent,
    DsoDropdownComponent,
    ErrorBannerComponent,
    GeneralErrorDialogComponent,
    ExistingWoWarningComponent,
    ExpansionPanelComponent,
    FileExtensionSelectionComponent,
    FilterOptionsComponent,
    FooterComponent,
    GranularitySelectionComponent,
    HeaderComponent,
    HeaderDropdownComponent,
    ErrorNoticeComponent,
    IconDropdownComponent,
    InformationDialogComponent,
    SuccessNoticeComponent,
    WarningNoticeComponent,
    InformationNoticeComponent,
    InputGroupComponent,
    InputLabelListComponent,
    LineGaugeChartRainbowComponent,
    LineGaugeChartTriColorComponent,
    ListTreeComponent,
    ListTreeDialogComponent,
    LongTextPopoverComponent,
    MeterConsumptionDropdownFilterComponent,
    MeterDownloadWizardComponent,
    MeterHeaderComponent,
    MeterHeaderItemComponent,
    MeterHeaderIconComponent,
    MeterListSelectionComponent,
    MeterSelectionComponent,
    MeterTableComponent,
    MeterWizardCollapsibleRowComponent,
    MultipurposeDropdownComponent,
    MunicipalitySelectionComponent,
    NotePopUpComponent,
    NotificationCardComponent,
    NotificationDialogComponent,
    OverlayLoaderComponent,
    PaginatorComponent,
    PasswordRequirementsComponent,
    ProgressBarComponent,
    ProgressDialogComponent,
    ShieldGuardIconComponent,
    SidebarComponent,
    SkeletonLoaderComponent,
    SplitButtonComponent,
    StatusChipComponent,
    StatusSnackBarComponent,
    ButtonSpinnerComponent,
    ConfirmationDialogV2Component,
    SvgIconComponent,
    UploadBoxComponent,
    UserProfileMenuComponent,
    EpCheckboxComponent,
    TermsOfUseDialogComponent,
    TermsAndConditionsDialogComponent,
    ErrorDialogComponent,
    EpInputFormFieldComponent,
    EpDateFormFieldComponent,
    EpToggleSwitchComponent,
    FeaturesIconAppComponent,
    EpFormSelectDropdownComponent,
    EpCardComponent,
    EpCardFooterComponent,
    //#endregion

    // # Meter Components
    MeterDataInfoDialogComponent,
    MeterTableComponent,
    // # End Meter Components

    //#region Directives & Pipes
    DigitOnlyDirective,
    DragndropDirective,
    DropzoneDirective,
    MatTooltipOnEllipsisDirective,
    StickyContainerDirective,
    AutofocusDirective,
    LazyLoadImgDirective,

    GetAddressPipe,
    GetMeterAddressPipe,
    GetAddressGpsPipe,
    GetEnergyTypeImagePipe,
    GetLocationNamePipe,
    GetMeterTypeImagePipe,
    InterpolatePipe,
    LocalDatePipe,
    LocalNumberPipe,
    NoCommaPipe,
    NoLeadingZeroPipe,
    ReplaceAllPipe,
    UrlSanitizerPipe,
    SafePipe,
    GetObisPipe,
    GetTariffTypePipe,
    //#endregion

    //#Reusable tooltip component and directive
    ReusableTooltipComponent,
    ReusableTooltipDirective
    //#End tooltip
  ],
  imports: [
    CalendarModule,
    CommonModule,
    ColorPickerModule,
    DragDropModule,
    EditorModule,
    FileUploadModule,
    FormsModule,
    MaterialModule,
    PaginatorModule,
    ProgressSpinnerModule,
    ReactiveFormsModule,
    RouterModule,
    ScrollingModule,
    TranslateModule
  ],
  exports: [
    //#region Modules
    CalendarModule,
    ColorPickerModule,
    DragDropModule,
    EditorModule,
    FileUploadModule,
    MaterialModule,
    ReactiveFormsModule,
    RouterModule,
    ScrollingModule,
    TranslateModule,
    PaginatorModule,
    ProgressSpinnerModule,
    //#endregion

    //#region Components
    AddressSearchComponent,
    BackButtonComponent,
    BannerAdvertisementComponent,
    ButtonSpinnerComponent,
    ClickToggleComponent,
    CollapsibleRowComponent,
    ConfirmationDialogComponent,
    ContactInfoComponent,
    ConsumptionFooterComponent,
    CustomTooltipComponent,
    EpInputIndicatorsComponent,
    EpSearchComponent,
    DashboardPanelComponent,
    DateSelectionComponent,
    DescriptionSnackbarComponent,
    DesignSettingsMenuComponent,
    DialogComponent,
    EpGenericDialogComponent,
    ContinueEditingDialogComponent,
    DropdownComponent,
    DropdownOptionComponent,
    EpDropdownComponent,
    EpDropdownOptionComponent,
    DropdownSelectionComponent,
    DsoDropdownComponent,
    ErrorBannerComponent,
    GeneralErrorDialogComponent,
    ExistingWoWarningComponent,
    ExpansionPanelComponent,
    FileExtensionSelectionComponent,
    FilterOptionsComponent,
    FooterComponent,
    GranularitySelectionComponent,
    HeaderComponent,
    HeaderDropdownComponent,
    ErrorNoticeComponent,
    IconDropdownComponent,
    InformationDialogComponent,
    SuccessNoticeComponent,
    WarningNoticeComponent,
    InformationNoticeComponent,
    InputGroupComponent,
    InputLabelListComponent,
    LineGaugeChartRainbowComponent,
    LineGaugeChartTriColorComponent,
    ListTreeComponent,
    ListTreeDialogComponent,
    LongTextPopoverComponent,
    MeterConsumptionDropdownFilterComponent,
    MeterDownloadWizardComponent,
    MeterHeaderComponent,
    MeterHeaderItemComponent,
    MeterHeaderIconComponent,
    MeterListSelectionComponent,
    MeterSelectionComponent,
    MeterTableComponent,
    MeterWizardCollapsibleRowComponent,
    MultipurposeDropdownComponent,
    MunicipalitySelectionComponent,
    NotePopUpComponent,
    NotificationCardComponent,
    NotificationDialogComponent,
    OverlayLoaderComponent,
    PaginatorComponent,
    PasswordRequirementsComponent,
    ProgressDialogComponent,
    ShieldGuardIconComponent,
    SidebarComponent,
    SkeletonLoaderComponent,
    SplitButtonComponent,
    StatusChipComponent,
    StatusSnackBarComponent,
    SvgIconComponent,
    UploadBoxComponent,
    UserProfileMenuComponent,
    EpCheckboxComponent,
    ErrorDialogComponent,
    EpInputFormFieldComponent,
    EpDateFormFieldComponent,
    EpToggleSwitchComponent,
    FeaturesIconAppComponent,
    EpFormSelectDropdownComponent,
    EpCardComponent,
    EpCardFooterComponent,
    //#endregion

    //#region Directives & Pipes
    DigitOnlyDirective,
    DragndropDirective,
    DropzoneDirective,
    MatTooltipOnEllipsisDirective,
    StickyContainerDirective,
    LazyLoadImgDirective,

    GetAddressPipe,
    GetMeterAddressPipe,
    GetAddressGpsPipe,
    GetEnergyTypeImagePipe,
    GetLocationNamePipe,
    GetMeterTypeImagePipe,
    InterpolatePipe,
    LocalDatePipe,
    LocalNumberPipe,
    NoCommaPipe,
    NoLeadingZeroPipe,
    ReplaceAllPipe,
    GetObisPipe,
    GetTariffTypePipe,
    //#endregion

    //#Reusable tooltip component and directive
    ReusableTooltipComponent,
    ReusableTooltipDirective
    //#End tooltip
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class SharedModule {}
